import { FormField } from '@components/ui/form';
import { FormItemWrapper } from '@components/ui/form/itemWrapper';
import { SelectContent, SelectItem } from '@components/ui/form/select';
import { ContactsDropdown } from '@services/dropdowns/companies/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropdownsContext } from '@contexts/dropdowns';
import { ExternalScheduleContactsInputsProps } from './types';
import { useFormContext } from 'react-hook-form';
import { useRescheduler } from '@contexts/rescheduler';
import { SelectInput } from '@components/ui/form/select/input';

export const ExternalScheduleContactsInputs = ({
  data,
  jobId,
  initialContactId,
  open,
  disabled,
}: ExternalScheduleContactsInputsProps) => {
  const {
    companies: { options, disabled: optionsDisabled },
    setParams,
  } = useDropdownsContext();
  const rescheduler = useRescheduler();
  const { control, setValue, watch } = useFormContext<{
    externalContactId: string;
  }>();

  const { t } = useTranslation('app');
  const [categoryId, setCategoryId] = useState(!initialContactId ? 'job' : '');
  const contactId = watch('externalContactId');
  const categoryChanged = useRef(false);

  const contacts = useMemo<ContactsDropdown[]>(() => {
    if (!categoryId) return [];
    if (categoryId === 'job') return options.workflowContacts || [];
    if (categoryId === 'project') return options.projectContacts || [];
    return options.companies.find((el) => el.id === categoryId)?.contacts || [];
  }, [categoryId, options]);

  useEffect(() => {
    if (!jobId) return;
    setParams({ companies: { jobId } });
  }, [jobId, setParams, open]);

  useEffect(() => {
    if (!contactId || categoryId) return;
    if (options.workflowContacts.find((el) => el.id === contactId)) {
      setCategoryId('job');
      return;
    }
    if (options.projectContacts.find((el) => el.id === contactId)) {
      setCategoryId('project');
      return;
    }
    const newCategoryId = options.companies.find((el) =>
      el.contacts.find((contact) => contact.id === contactId),
    )?.id;
    if (newCategoryId) setCategoryId(newCategoryId);
  }, [options, contactId, categoryId]);

  useEffect(() => {
    if (open) categoryChanged.current = false;
  }, [open]);

  useEffect(() => {
    if (categoryChanged.current) return;
    if (!contactId && initialContactId) setValue('externalContactId', initialContactId);
  }, [contactId, initialContactId, setValue]);

  useEffect(() => {
    if (contacts.length !== 1 || contactId) return;
    setValue('externalContactId', contacts[0].id);
  }, [contacts, contactId, setValue]);

  return (
    <div className="flex w-full flex-1 gap-4">
      <FormItemWrapper label={t('forms.category.label')} select required>
        <SelectInput
          name="categoryId"
          value={categoryId}
          onChange={(v) => {
            if (!v || v === categoryId) return;
            categoryChanged.current = true;
            setValue('externalContactId', '');
            setCategoryId(v);
          }}
          options={[
            ...options.companies.map((el) => ({ name: el.name, value: el.id })),
            { name: t('schedules.contacts.job'), value: 'job' },
            { name: t('schedules.contacts.project'), value: 'project' },
          ]}
          children={
            <SelectContent>
              <SelectItem value="job">{t('schedules.contacts.job')}</SelectItem>
              <SelectItem value="project">{t('schedules.contacts.project')}</SelectItem>
              {!optionsDisabled && (
                <>
                  <p className="cursor-not-allowed select-none px-2 py-1.5 text-sm">
                    {t('schedules.contacts.external')}
                  </p>
                  {options.companies.map(({ id, name }) => (
                    <SelectItem className="pl-4" key={id} value={id}>
                      {name}
                    </SelectItem>
                  ))}
                </>
              )}
            </SelectContent>
          }
          disabled={optionsDisabled || disabled || (Boolean(contactId) && !categoryId)}
          placeholder={t('forms.default.select.placeholder')}
        />
      </FormItemWrapper>
      <FormField
        control={control}
        name="externalContactId"
        render={({ field: { value, onChange } }) => (
          <FormItemWrapper label={t('forms.name.label')} select required>
            <SelectInput
              name="externalContactId"
              value={contacts.length ? value : ''}
              onChange={(v) => {
                if (v && data && rescheduler) rescheduler.removeSavedSchedule(data);
                onChange(v);
              }}
              options={contacts.map((el) => ({ name: el.fullName || '', value: el.id }))}
              disabled={!categoryId || disabled || contacts.length === 0}
              placeholder={t('forms.default.select.placeholder')}
            />
          </FormItemWrapper>
        )}
      />
    </div>
  );
};
